import styled from '@emotion/styled'
import React, { useEffect, useState } from 'react'

import { isContentfulPreviewFrame } from '../helpers/isContentfulPreviewFrame'
import { refreshGatsbyContentful, resumeGatsbyContentfulRefresh } from '../helpers/refreshGatsbyContentful'
import { useGatsbyContentfulRefreshStatus } from '../hooks/useGatsbyContentfulRefreshStatus'

// Single row: label + refresh button. `Layout` reserves this much space for it.
export const PREVIEW_BANNER_HEIGHT = 32

// Desktop only, matching the breakpoint the headers switch on.
export const PREVIEW_BANNER_BREAKPOINT = 900

/**
 * True when the preview banner should be shown (and its space reserved): the page is embedded in
 * the Contentful editor's live-preview iframe. That check needs `window`, so it settles after
 * mount to keep server and client markup identical.
 */
export const usePreviewBanner = (): boolean => {
  const [inEditorFrame, setInEditorFrame] = useState(false)

  useEffect(() => setInEditorFrame(isContentfulPreviewFrame()), [])

  return inEditorFrame
}

const Banner = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: ${PREVIEW_BANNER_HEIGHT}px;
  z-index: 200;
  display: none;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 0 12px;
  box-sizing: border-box;
  background-color: #ffb300;
  background-image: repeating-linear-gradient(
    45deg,
    rgba(0, 0, 0, 0.12) 0,
    rgba(0, 0, 0, 0.12) 10px,
    transparent 10px,
    transparent 20px
  );
  color: #1f1f1f;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-align: center;
  overflow: hidden;

  @media (min-width: ${PREVIEW_BANNER_BREAKPOINT}px) {
    display: flex;
  }
`

const RefreshButton = styled.button`
  flex-shrink: 0;
  height: 22px;
  padding: 0 10px;
  border: 1px solid rgba(0, 0, 0, 0.35);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.85);
  color: #1f1f1f;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #fff;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`

export const PreviewBanner: React.FC = () => {
  const isRefreshing = useGatsbyContentfulRefreshStatus()

  // Webpack reloads the iframe part-way through a re-source (the dev bundle's hash changes, so HMR
  // reports "Cannot find update (Full reload needed)"). The job is server-side and unaffected, so
  // reattach to it here: without this the banner vanished mid-rebuild and the final reload never
  // ran, making a refresh that was still going look like it had quietly failed.
  useEffect(() => {
    if (!isContentfulPreviewFrame()) return

    let cancelled = false

    resumeGatsbyContentfulRefresh()
      .then(resumed => {
        if (resumed && !cancelled) window.location.reload()
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [])

  const handleRefresh = () => {
    if (isRefreshing) return

    refreshGatsbyContentful()
      .then(() => {
        // New drafts only appear after createPages; a reload picks up the route / query data.
        //
        // This used to be unreachable in practice: the refresh POST was held open for the whole
        // re-source, so Cloudflare's 100s origin timeout returned 524 on a re-source that was
        // still running and about to succeed, the promise rejected, and the page never reloaded —
        // the editor saw a failure and a stale page for work the server had actually completed.
        window.location.reload()
      })
      .catch(() => undefined)
  }

  return (
    <Banner role="status" data-testid="preview-banner">
      <span>LIVE PREVIEW MODE</span>
      <RefreshButton type="button" data-testid="preview-banner-refresh" disabled={isRefreshing} onClick={handleRefresh}>
        {isRefreshing ? 'Refreshing…' : 'Refresh preview'}
      </RefreshButton>
    </Banner>
  )
}

export default PreviewBanner
